import { useState } from 'react';
import type { ChatMessage } from '@/models';
import { APIRoutes } from '@/services/api.routes';
import Modal from '@/components/ui/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@/fontawesome';

interface UserMessageProps {
  message: ChatMessage;
  debugMode?: boolean;
}

export default function UserMessage({ message, debugMode = false }: UserMessageProps) {
  const hasImages = message.imageBlobIDs && message.imageBlobIDs.length > 0;
  const hasText = message.content.trim().length > 0;
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  return (
    <div className="flex justify-end">
      <div className="max-w-2xl relative">
        {debugMode && (message.model || message.llmLevel) && (
          <div className="absolute top-2 right-2 flex gap-2 z-10">
            {message.model && (
              <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                {message.model}
              </span>
            )}
            {message.llmLevel && (
              <span className="px-2 py-1 text-xs bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded">
                {message.llmLevel}
              </span>
            )}
          </div>
        )}
        <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl px-4 py-3 shadow-sm">
          {/* Images */}
          {hasImages && (
            <div className={`${hasText ? 'mb-3' : ''}`}>
              <div className="grid gap-2" style={{
                gridTemplateColumns: message.imageBlobIDs.length === 1
                  ? '1fr'
                  : message.imageBlobIDs.length === 2
                    ? 'repeat(2, 1fr)'
                    : 'repeat(auto-fit, minmax(150px, 1fr))'
              }}>
                {message.imageBlobIDs.map((blobId, index) => (
                  <div key={blobId} className="relative group">
                    <img
                      src={APIRoutes.getBlobContentUrl(blobId)}
                      alt={`Attachment ${index + 1}`}
                      className="w-full h-auto rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:opacity-90 transition-opacity"
                      style={{
                        maxHeight: message.imageBlobIDs.length === 1 ? '300px' : '200px',
                        objectFit: 'cover'
                      }}
                      onClick={() => {
                        setSelectedImageUrl(APIRoutes.getBlobContentUrl(blobId));
                      }}
                      onError={(e) => {
                        // Handle image load errors with fallback SVG
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIxNTAiIGZpbGw9IiNGM0Y0RjYiLz48dGV4dCB4PSIxMDAiIHk9Ijc1IiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzlDQTNBRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+QmlsZCBuaWNodCBnZWxhZGVuPC90ZXh0Pjwvc3ZnPg==';
                        target.alt = 'Bild nicht geladen';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Text Content */}
          {hasText && (
            <div className="whitespace-pre-wrap break-words text-gray-900 dark:text-white">
              {message.content}
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      <Modal
        isOpen={selectedImageUrl !== null}
        onClose={() => setSelectedImageUrl(null)}
        title="Bild"
        icon={<FontAwesomeIcon icon={faImage} className="w-5 h-5 text-blue-600" />}
        maxWidth="fullscreen"
      >
        <div className="flex items-center justify-center h-full p-4">
          {selectedImageUrl && (
            <img
              src={selectedImageUrl}
              alt="Vollbild"
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: 'calc(100vh - 200px)' }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
